// Core packages

// Third party packages

// Custom packages

/**
 * Where the stickers already printed of the archive go once the new archive is live (NEW-A3 FE).
 *
 * A file of its own, WITHOUT IMPORTS on purpose: `PublicQrV3LegacyGuard` sits on the routes of
 * AppRoutingModule, which are in the initial bundle, and anything this file imported would land
 * there too. The page itself is lazy.
 *
 * @since 3.0.0
 */

/**
 * The legacy formats of an archive QR, as the routes of the legacy public page name them
 * (`data.publicQrLegacy`), the ones the new archive answers once `archiveV3Live` is on:
 *  - `doc`: `/qrcode/<archive>/<section>/<docId|generic>/<key>/<subdocid>`;
 *  - `section`: `/qrcode/<archive>/docstructure/<area>` and
 *    `/qrcode/<archive>/docsubstructure/<area>/<section>[/<structureId>]`;
 *  - `sectionold`: `/qrcode/<old property>/docstructureext/<category number>`.
 */
export type PublicQrLegacyFormat = "doc" | "section" | "sectionold";

/** Key of the route data naming the legacy format of a route of the legacy public page */
export const PUBLIC_QR_LEGACY_DATA_KEY = "publicQrLegacy";

/**
 * Where a sticker already printed goes once the new archive is live: the address of the new public
 * page that reads it (`/qrcode/v3/legacy/...`), from the parameters of the legacy route; `null` to
 * stay on the legacy page — the switch off, or a route that is not an archive QR.
 *
 * Only what the back-end reads travels: for a document, the archive and `subdocid` (section, docId and
 * key are not needed); for a section, the archive and the keys. Whether they are valid is for the new
 * page to say, which answers «not found» to an address that names nothing.
 *
 * @since 3.0.0
 *
 * @param live   Whether the new archive is live (@see isArchiveV3Live)
 * @param format `data.publicQrLegacy` of the route
 * @param params The parameters of the legacy route
 */
export function publicQrLegacyRedirectCommands(
  live: boolean,
  format: unknown,
  params: { [name: string]: unknown } | null | undefined
): string[] | null {
  if (!live) {
    return null;
  }
  const p: { [name: string]: unknown } = params && typeof params === "object" ? params : {};
  const text = (value: unknown): string => (typeof value === "string" ? value : "");
  switch (format) {
    case "doc":
      return text(p.documentId) && text(p.subdocid)
        ? ["/qrcode", "v3", "legacy", "doc", text(p.documentId), text(p.subdocid)]
        : null;
    case "section": {
      if (!text(p.documentId) || !text(p.section)) {
        return null;
      }
      const commands = ["/qrcode", "v3", "legacy", "section", text(p.documentId), text(p.section)];
      if (text(p.subSection)) {
        commands.push(text(p.subSection));
        if (text(p.structureId)) {
          commands.push(text(p.structureId));
        }
      }
      return commands;
    }
    case "sectionold":
      return text(p.propertyId) && text(p.sectionold)
        ? ["/qrcode", "v3", "legacy", "sectionold", text(p.propertyId), text(p.sectionold)]
        : null;
    default:
      return null;
  }
}
